/**
 * Studio v2 — the token contract (`--xv-art-*`).
 *
 * Authority: `docs/architecture/studio-token-contract.md` §5.1 (ArtTokenMap),
 * §4.3 (BrandKit), §4.4 (BRAND_KIT_ALLOWED_TOKENS).
 *
 * Every artifact KIND (deck slides, doc sections, email blocks) renders against
 * the SAME `--xv-art-*` CSS variables defined here. A theme is a full token set
 * + a layout language; a brand kit is a whitelisted partial override. This module
 * is the pure, web-importable presentation contract — no framework imports.
 */
/**
 * The REQUIRED ArtTokenMap keys — exactly the non-optional fields above. A theme
 * MUST provide every one of these; the contract test asserts it for every
 * registered theme (the analogue of the report engine's themeTokens contract).
 *
 * Kept as a runtime `as const` array (not derived from the type — TS cannot
 * reflect interface keys at runtime) so the contract test can iterate it.
 * Must stay in sync with the non-optional fields of ArtTokenMap above.
 */
export const REQUIRED_ART_TOKENS = [
    '--xv-art-font-display',
    '--xv-art-font-body',
    '--xv-art-bg',
    '--xv-art-bg-surface',
    '--xv-art-ink',
    '--xv-art-ink-secondary',
    '--xv-art-ink-muted',
    '--xv-art-accent',
    '--xv-art-accent-dim',
    '--xv-art-border',
    '--xv-art-border-strong',
    '--xv-art-measure',
    '--xv-art-stat-ink',
    '--xv-art-stat-positive',
    '--xv-art-stat-negative',
];
/**
 * The brand-kit override allowlist (token-contract §4.4, FROZEN).
 *
 * A brand kit may set ONLY this whitelisted subset of §2 tokens. The resolver
 * hard-drops any disallowed token a kit declares (defense in depth) so layout
 * (§2.6) and treatment geometry can never leak from a kit — "the theme keeps
 * its layout", made mechanical.
 */
export const BRAND_KIT_ALLOWED_TOKENS = [
    '--xv-art-accent',
    '--xv-art-accent-ink',
    '--xv-art-accent-2',
    '--xv-art-accent-dim',
    '--xv-art-ink',
    '--xv-art-ink-secondary',
    '--xv-art-ink-muted',
    '--xv-art-ink-inverse',
    '--xv-art-bg',
    '--xv-art-bg-surface',
    '--xv-art-bg-inverse',
    '--xv-art-border',
    '--xv-art-border-strong',
    '--xv-art-font-display',
    '--xv-art-font-body',
    '--xv-art-font-mono',
    '--xv-art-font-weight-display',
    '--xv-art-font-weight-body',
    '--xv-art-stat-ink',
    '--xv-art-stat-positive',
    '--xv-art-stat-negative',
    '--xv-art-highlight-ink',
    '--xv-art-highlight-bg',
    '--xv-art-logo-url',
    '--xv-art-logo-aspect',
    '--xv-art-logo-height',
    '--xv-art-logo-invert',
];
/** Fast membership set for the resolver's `pick(brandKit.tokens, …)` step. */
export const BRAND_KIT_ALLOWED_TOKEN_SET = new Set(BRAND_KIT_ALLOWED_TOKENS);
/**
 * Keep only the keys a brand kit is allowed to set (token-contract §4.4),
 * dropping any non-string value. THE single allowlist down-filter — shared by
 * the resolver, the brand-kit write path (POST/PATCH), and the entity→wire
 * mappers — so a kit can never carry a layout/treatment token ("the theme keeps
 * its layout", made mechanical) and the four former copies can't drift. A token
 * value is always a CSS string, so the `typeof === 'string'` guard is exact.
 */
export function filterBrandKitTokens(tokens) {
    const out = {};
    const sink = out;
    for (const key of Object.keys(tokens ?? {})) {
        if (BRAND_KIT_ALLOWED_TOKEN_SET.has(key)) {
            const value = tokens[key];
            if (typeof value === 'string')
                sink[key] = value;
        }
    }
    return out;
}
/** Cap on a kit's `extraColors` (#5331, §4.3): 5 user-added swatches. */
export const BRAND_KIT_EXTRA_COLORS_MAX = 5;
/**
 * Sanitize a kit's `extraColors` (#5331): keep only parseable hex colors
 * (`#rgb`/`#rrggbb`, any case) normalized to lowercase `#rrggbb`, de-duplicated,
 * capped at `BRAND_KIT_EXTRA_COLORS_MAX`. THE single down-filter — shared by
 * the write path (POST/PATCH), the entity→wire mapper, and the resolver's
 * seriesPalette derivation — so a malformed/oversized array can never surface.
 * Anything non-array (or an array of garbage) collapses to `[]`.
 */
export function filterExtraColors(input) {
    if (!Array.isArray(input))
        return [];
    const out = [];
    for (const value of input) {
        if (typeof value !== 'string')
            continue;
        const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(value.trim().toLowerCase());
        if (!m)
            continue;
        const h = m[1];
        const hex = h.length === 3 ? `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}` : `#${h}`;
        if (out.includes(hex))
            continue;
        out.push(hex);
        if (out.length >= BRAND_KIT_EXTRA_COLORS_MAX)
            break;
    }
    return out;
}
//# sourceMappingURL=art-tokens.js.map